import type { CanonicalDocument, Commentary, DocNode } from './schema';

// A compact, human "what changed" line for the History panel — computed by
// diffing a new snapshot against its predecessor at save time and stored on the
// revision. Deliberately coarse (counts, not a full visual diff): enough to
// answer "what did this version change?" at a glance.

// Exported so the richer visual diff (`document-diff.ts`) tests against the SAME
// definition of "changed" the stored one-line summary uses — one source of truth.
export function nodeChanged(a: DocNode, b: DocNode): boolean {
  return (
    a.type !== b.type ||
    a.parentId !== b.parentId ||
    a.orderIndex !== b.orderIndex ||
    JSON.stringify(a.lines) !== JSON.stringify(b.lines)
  );
}

export function commentaryChanged(a: Commentary, b: Commentary): boolean {
  return (
    a.body !== b.body ||
    a.kind !== b.kind ||
    a.audience !== b.audience ||
    a.targetNodeId !== b.targetNodeId ||
    JSON.stringify(a.extraTargets) !== JSON.stringify(b.extraTargets)
  );
}

function countById<T extends { id: string }>(
  prev: T[],
  next: T[],
  changed: (a: T, b: T) => boolean,
) {
  const prevById = new Map(prev.map((x) => [x.id, x]));
  const nextById = new Map(next.map((x) => [x.id, x]));
  let added = 0;
  let removed = 0;
  let edited = 0;
  for (const [id, n] of nextById) {
    const p = prevById.get(id);
    if (!p) added++;
    else if (changed(p, n)) edited++;
  }
  for (const id of prevById.keys()) if (!nextById.has(id)) removed++;
  return { added, removed, edited };
}

function phrase(n: number, singular: string): string {
  return `${n} ${singular}${n === 1 ? '' : 's'}`;
}

/**
 * One-line change summary of `next` relative to `prev` (e.g. "2 blocks edited ·
 * 1 block added · 1 note added"). Returns "minor change" if nothing material
 * differs (e.g. only fractional re-index noise was already filtered out).
 */
export function summarizeRevisionChange(prev: CanonicalDocument, next: CanonicalDocument): string {
  const n = countById(prev.nodes, next.nodes, nodeChanged);
  const c = countById(prev.commentary, next.commentary, commentaryChanged);
  const docChanged =
    prev.document.title !== next.document.title ||
    prev.document.subtitle !== next.document.subtitle ||
    JSON.stringify(prev.document.notes) !== JSON.stringify(next.document.notes);

  const parts: string[] = [];
  if (n.added) parts.push(`${phrase(n.added, 'block')} added`);
  if (n.removed) parts.push(`${phrase(n.removed, 'block')} removed`);
  if (n.edited) parts.push(`${phrase(n.edited, 'block')} edited`);
  if (c.added) parts.push(`${phrase(c.added, 'note')} added`);
  if (c.removed) parts.push(`${phrase(c.removed, 'note')} removed`);
  if (c.edited) parts.push(`${phrase(c.edited, 'note')} edited`);
  if (docChanged) parts.push('details changed');

  return parts.length ? parts.join(' · ') : 'minor change';
}
